import React, { useEffect, useState } from "react";
import { handleKeyboardEventBehavior } from "@qlik/qlik/src/lib/util/helpers";

/**
 * `DynamicCheckboxFilterGroup` is a React component that renders a list of checkboxes for filtering provided list of data items.
 * Each checkbox represents a filter option within a specific category.
 *
 * @component
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.category - The category to which the filters belong.
 * @param {Object} props.filters - The current active filters.
 * @param {function} props.setFilters - The function to update the active filters.
 * @param {function} props.setFilters - The function to update the last interacted category.
 * @param {number} props.index - The index of the current parent category being rendered.
 *
 * @returns {JSX.Element} A list of checkboxes for filtering resources.
 */
const DynamicCheckboxFilterGroup = ({
    category,
    filters,
    setFilters,
    setCurrentCategory,
    index,
    expandByDefault = false,
}) => {
    const handleCheckboxChange = (event, filterValue) => {
        setCurrentCategory(category.id);
        if (event.target.checked) {
            setFilters((prev) => {
                const newFilter = [...prev[category.id]];
                newFilter.push(filterValue);
                const updatedFilter = { ...prev, page: 0, [category.id]: newFilter };
                return updatedFilter;
            });
        } else {
            setFilters((prev) => {
                const newFilter = prev[category.id].filter((val) => val !== filterValue);
                const updatedFilter = { ...prev, page: 0, [category.id]: newFilter };
                return updatedFilter;
            });
        }
    };

    const isChecked = (filterValue) => filters[category.id]?.includes(filterValue);

    const [isFilterCategoryExpanded, setIsFilterCategoryExpanded] = useState(expandByDefault);

    const handleFilterCategoryClick = () => {
        setIsFilterCategoryExpanded(!isFilterCategoryExpanded);
    };

    // If the 'expandedByDefault' prop isn't set, then only expand the first filter by default
    useEffect(() => {
        setIsFilterCategoryExpanded(expandByDefault);
        if (index === 0 && !expandByDefault) setIsFilterCategoryExpanded(true);
    }, [expandByDefault, index]);

    return (
        <div className="mt-2.5">
            <p
                role="button"
                onClick={handleFilterCategoryClick}
                tabIndex={0}
                onKeyDown={(e) => {
                    handleKeyboardEventBehavior(e, handleFilterCategoryClick);
                }}
                className={`tag-2 pt-5 pb-4.75 border-t uppercase bg-no-repeat bg-right-center hover:cursor-pointer ${index} ${
                    isFilterCategoryExpanded
                        ? "active bg-downArrowActive border-green text-blue"
                        : "bg-downArrow border-stone text-gray"
                }`}
            >
                {category.title}
            </p>
            <div
                className={`grid transition-all duration-300 ${
                    isFilterCategoryExpanded ? "grid-rows-1 pb-4.25 -mt-[1px] opacity-100" : "grid-rows-[0fr] opacity-0"
                }`}
            >
                <div className="overflow-hidden">
                    {category?.subTerms?.map((filter) => (
                        <div key={filter.id} className="relative mb-3.25">
                            <label
                                htmlFor={filter.id}
                                className={`body pl-9.5 text-sm font-light ${
                                    filter.isDisabled ? "text-stone" : "text-blue cursor-pointer"
                                }`}
                            >
                                {filter.title}
                                <input
                                    className="absolute h-0 w-0 cursor-pointer opacity-0"
                                    id={filter.id}
                                    disabled={filter.isDisabled}
                                    type="checkbox"
                                    value={filter.title}
                                    checked={isChecked(filter.title)}
                                    onChange={(e) => handleCheckboxChange(e, filter.title)}
                                />
                                <span
                                    className={`absolute left-0 top-0.5 w-5.5 h-5.5 border rounded-xxs transition duration-300 ${
                                        filter.isDisabled ? "border-gainsboro" : ""
                                    } ${
                                        isChecked(filter.title)
                                            ? "bg-green border-green bg-tick bg-no-repeat bg-center"
                                            : "border-stone"
                                    }`}
                                />
                            </label>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default DynamicCheckboxFilterGroup;
